import cn from '@/lib/cs';
import { memo, useId } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

const WordMarkLogo = memo(
	({
		className,
		withShadow,
		isAnimated = true,
		animationDelay = 0.2,
	}: {
		className?: string;
		withShadow?: boolean;
		isAnimated?: boolean;
		animationDelay?: number;
	}) => {
		const reduceMotion = useReducedMotion();

		return (
			<motion.div
				className={cn('relative w-fit', className)}
				initial={
					isAnimated
						? {
								scale: reduceMotion ? 1 : 0.8,
								opacity: reduceMotion ? 1 : 0,
								 
						  }
						: undefined
				}
				whileInView={
					isAnimated
						? {
								scale: 1,
								opacity: 1,
								transition: { delay: animationDelay },
								 
						  }
						: undefined
				}
			>
				{withShadow && (
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 644 361"
						preserveAspectRatio="xMidYMid meet"
						className="absolute inset-x-0 size-full translate-x-1 translate-y-1 text-accentOrCompatDarkest"
						aria-hidden
					>
						<WordMarkLogoSVGContent color="currentColor" />
					</svg>
				)}
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 644 361"
					preserveAspectRatio="xMidYMid meet"
					role="img"
					aria-label="SoortSafari woordlogo met appeltje van Het Klokhuis"
					className="relative size-full text-accentOrCompatLightest"
				>
					<WordMarkLogoSVGContent color="currentColor" />
				</svg>
			</motion.div>
		);
	}
);

const WordMarkLogoSVGContent = ({ color }: { color: string }) => {
	const clipPathId = useId();
	return (
		<>
			<g clipPath={`url(#${clipPathId})`}>
				<mask
					id="mask0_863_9623"
					style={{ maskType: 'luminance' }}
					width="644"
					height="361"
					x="0"
					y="0"
					maskUnits="userSpaceOnUse"
				>
					<path fill="white" d="M0 0h644v361H0V0z"></path>
				</mask>
				<g fill={color} mask="url(#mask0_863_9623)">
					<path d="M398 127c-3 8 2 46-6 47-9-5-23 5-29-1-2-18-1-34 3-51-3-28 3-55 3-82 3-4 4-11 10-12l39 2c13 1 21 13 32 20 4 12 2 26 3 39-2 12-11 21-21 27 1 10 8 21 11 32-1 12 9 19 12 30-10 0-21-5-31-1-3 1-3 0-4-2-1-11-12-17-13-28-2-7-3-15-9-20zm24-50c-1-7-1-14-3-20-3-1-6-5-9-3-4 4-10 8-11 14-7 32 10 41 23 9zm-25 200c-8 6-3 34-8 46-2 9-18 3-25 5-11-9-1-42-3-56-5-25 2-50-2-76 0-4-2-9 0-12 16-2 33-5 48-1 8 3 16 7 25 8 7 1 8 1 9 9 22 76-39 53 3 105 18 25-11 15-27 19-16-14-13-29-20-47zm19-52c1-15-12-16-24-18-2 5 2 13 1 18 2 7-1 13 1 19 3 8 8 4 14 2 5-6 6-14 8-21zM268 76c-5-40-15-39-49-51-13-2-23 13-35 19-8 26-6 58-13 86-3 12 8 23 12 34 4 8 34 12 42 8 40-14 30-64 43-96zm-35-6l-1 10c-2 0-2 54-19 64-2 1-5 5-6 2-2-6-6-13-5-20 1-11 8-21 5-32-2-5-1-9 1-14 6-17 15-43 25-10zm127-28c-2-14-19-16-28-24-13-11-32-1-42 10-23 17-18 49-16 75 2 13-6 32 2 42 11 7 21 12 34 12 10 5 20 2 29-5 8-15 13-32 17-48-1-21 4-41 4-62zm-36 41c-1 10 2 18-2 27-5 8-17 29-24 14-4-9 3-20 3-29 3-10-2-21-1-32 6-9 8-29 21-20 7 7 4 30 3 40z"></path>
					<path d="M165 37c-21 28-22 11-48 4-1 5-5 12-5 18 5 4 9 20 15 18 13-9 25 3 39 6 7 31 5 51-21 73-12 6-26 7-39 11-7 3-13-3-20-5-4-6-11-11-11-18 15-28 22-11 41 0 7-2 12-8 18-12 4-5-2-13-3-19-7-5-18-2-25-9-18-8-34-26-28-47 3-10-2-22 10-26 13-7 24-28 40-23 26 10 22 2 37 29zm179 178c-4 17 2 34 2 51-4 25 5 51 3 76 1 25 6 18-18 17-18-3 1-24-12-33-30-6-20-13-29 8-12 23-3 19-30 15-12-2 6-22 7-30 1-19 12-33 16-51 4-15 4-62 24-60 11 3 34-7 37 7zm-24 80c-4-7-2-14-1-22 0-10-5-19-2-29-6 15-9 34-14 50 6 0 11 2 17 1zm-209 22c-5-1-15 4-15-2 0-9 0-19 4-27 7-25 2-52 13-77 1-15-2-31 10-42 5-3 10 3 16 1 6-1 14-7 19 0s6 15 6 23c4 18 12 35 11 54 1 8 6 13 9 20 4 13 2 26 7 39 0 4-6 3-8 5-6 0-13 7-18 3-12-9-5-30-16-37-4 2-12 0-14 4 1 12-12 18-12 30 0 7-7 8-12 6zm29-116c-6 17-1 37-11 54 5-1 22 4 19-4-3-17-4-34-8-50zm349 15c-43-3-27 8-30-28 29-13 61 7 91 0 6 1 2 16 4 21 4 20-20 9-31 7-8 22-6 50-7 74 0 6-8 20 3 20 7 2 26-3 28 5-3 6-1 20-11 20-17 2-34-2-52-4-7-2-34 4-30-7 3-25 6-18 26-19 6-9 4-25 8-36 3-18-7-36 1-53zM60 330c-35-6-25 2-57-19-8-5 2-17 2-24 5-7 16 4 22 6 5 2 7 10 13 7 4-3 13-2 16-7 4-11-4-17-13-23-11-5-5-20-15-28-14-9-3-26-2-39 4-8 23-21 31-15 8 4 18-1 26 3 7 4 20 7 21 16-12 35-15-3-48 12-9 5-6 12-2 19 3 4 8 3 12 4 11 9 24 21 27 35 6 18-8 31-14 47-4 7-12 5-19 6zm174-114c-2 12-1 23 0 36 10 1 21-4 31-1 3 3 8 5 9 9-1 4 1 11-3 12-14-3-23 7-35 8-6 4 2 17-1 24-2 8-4 17-4 26-2 5-12 4-17 4-3-4-10-1-11-6-1-11 1-23 1-34-3-18-11-36-5-55 4-16-2-31-4-46 4-15 29-4 40-8 13-4 26-1 39-1 6 1 11 10 12 16-3 3-2 11-7 12-9 1-18 0-26 2-7 2-12 6-19 2zM443 30c-6 2-11-10-8-16 2-2-3-6 1-8 5-7 17 2 25-2 12-7 25-3 38-2 10-1 20-3 30 0 8 2 1 13 1 19-1 6-10 8-16 8-4 0-12-3-12 4-1 19-3 37-1 56 1 13-4 25-3 38 0 8 4 18-1 26-6 16-16 8-29 5-3-2-4-3-3-7 1-15-3-30 0-44 5-24 1-47 5-70 0-2 2-6-1-6l-26-1zm201 57c0-17-26-41-45-35l3-23-10-1-4 22c-19-15-50 5-54 15 17 20 22 50 0 69 12 31 66 41 88 15-3-7-7-16-7-24 9-19 5-29 29-38zm-8-2c-3 0-4 2-5 4 0-3-2-4-4-6l9 2zm-26-4h7c-6 4-9 3-12-3l5 3zm-10-2l7 8c-5 4-9 8-11 14-1-10-10-17 4-22zm-7-2c-2 0-4 1-5 3l-4-5 9 2zm-11 0c7 8-6 10-8 19 0-6-1-11-4-16l9-6 3 3zm-14-5l5 1c-2 0-4 1-6 4l-4-7 5 2zm-23 0l-3-4 8 1-5 3zm3 5c5-10 11-5 16 2-6 4-8 9-11 15 0-5-2-13-5-17zm-6 55c2-1 5-4 5-7 1 4 3 6 6 9l-11-2zm11-14c2-12 4-27 15-35-2 26 9 24-11 50l-4-15zm12 18l-6-1c5-2 9-4 10-9 0 4 1 10 3 13l-7-3zm13 1c-3-8-6-16-2-25-4-9 5-16 10-24 10 16 6 37-8 49zm10 4h-5c2-2 7-6 7-9 3 4 4 7 4 11l-6-3v1zm9-6c-5-16-2-33 12-43 5 24 9 30-10 51l-2-8zm19 12l-11-2c4-2 6-4 7-8 0 3 0 8 4 10zm4-49l-3 6c-3-5 0-13-5-16 3-1 7-2 9-6 2 4 5 7 8 9l-9 7z"></path>
				</g>
			</g>
			<defs>
				<clipPath id={clipPathId}>
					<path fill="white" d="M0 0H644V361H0z"></path>
				</clipPath>
			</defs>
		</>
	);
};

export default WordMarkLogo;
